/**
 * 🎨 GALLERY FLOATING TOOLBAR 2025
 * 
 * Floating Toolbar für Gallery Element
 * - Image Upload & Management
 * - Layout Modes (Masonry, Grid, Floating, etc.)
 * - Caption Settings
 * - Styling Options
 * - Library Integration
 * 
 * ✅ Matched to ButtonFloatingToolbar Design:
 * - Drag & Drop functionality
 * - Icon-based sections
 * - Pill-shaped → rounded transition
 * - Desktop/Mobile toggles in sections
 * - No title header
 */

import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useFont } from '../../Design/FontContext';
import { titleFonts, textFonts } from '../../utils/fontsHelper';
import './GalleryFloatingToolbar.css';

function GalleryFloatingToolbar({
    isEditMode,
    isVisible,
    element,
    onStyleChange,
    onClose,
    colors = {},
    isMobileLayout = false,
    galleryRef,
    brandFonts = {} // ✅ NEW: Brand Fonts (globalBigFont, globalSmallFont)
}) {
    // 🎯 Font Context für Custom Fonts
    const fonts = useFont();
    const [showToolbar, setShowToolbar] = useState(false);
    const [toolbarPosition, setToolbarPosition] = useState({ top: 80, left: window.innerWidth / 2 });
    const toolbarRef = useRef(null);
    
    // Expandable Sections State
    const [expandedSection, setExpandedSection] = useState(null); 
    // 'images' | 'layout' | 'text' | 'styling' | 'library'
    
    // 🎯 Device Toggle State
    const [device, setDevice] = useState(isMobileLayout ? 'mobile' : 'desktop');
    
    // 🎯 Dragging State
    const [isDragging, setIsDragging] = useState(false);
    const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
    const [isManuallyPositioned, setIsManuallyPositioned] = useState(false);
    
    // Image Upload State
    const [isUploadingImage, setIsUploadingImage] = useState(false);
    
    // Selected Image for Editing
    const [selectedImageId, setSelectedImageId] = useState(null);
    
    // 🎯 Drag Handlers
    const handleMouseDown = (e) => {
        e.preventDefault();
        e.stopPropagation();
        
        setIsDragging(true);
        setDragOffset({
            x: e.clientX - toolbarPosition.left,
            y: e.clientY - toolbarPosition.top
        });
    };
    
    const handleMouseMove = (e) => {
        if (!isDragging) return;
        
        const newLeft = e.clientX - dragOffset.x;
        const newTop = e.clientY - dragOffset.y;
        
        // Bounds checking
        const maxLeft = window.innerWidth - (toolbarRef.current?.offsetWidth || 400);
        const maxTop = window.innerHeight - (toolbarRef.current?.offsetHeight || 300);
        
        setToolbarPosition({
            left: Math.max(0, Math.min(newLeft, maxLeft)),
            top: Math.max(0, Math.min(newTop, maxTop))
        });
    };
    
    const handleMouseUp = () => {
        setIsDragging(false);
        setIsManuallyPositioned(true);
    };
    
    // ✅ Drag Event Listeners
    useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            
            return () => {
                document.removeEventListener('mousemove', handleMouseMove);
                document.removeEventListener('mouseup', handleMouseUp);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isDragging]);
    
    // Sync device toggle with isMobileLayout
    useEffect(() => {
        setDevice(isMobileLayout ? 'mobile' : 'desktop');
    }, [isMobileLayout]);
    
    // Sync showToolbar with isVisible
    useEffect(() => {
        if (isEditMode && isVisible) {
            setShowToolbar(true);
        } else {
            setShowToolbar(false);
            setExpandedSection(null);
        }
    }, [isEditMode, isVisible]);
    
    // Update Position on Resize
    useEffect(() => {
        if (!showToolbar || isManuallyPositioned) return;
        
        const handleResize = () => {
            if (!expandedSection) {
                const left = window.innerWidth / 2;
                setToolbarPosition(prev => ({ ...prev, left }));
            }
        };
        
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, [showToolbar, expandedSection, isManuallyPositioned]);
    
    // Handle Outside Click - Close toolbar
    useEffect(() => {
        if (!showToolbar || !isVisible) return;
        
        const handleClickOutside = (event) => {
            const clickedInToolbar = toolbarRef.current && toolbarRef.current.contains(event.target);
            const clickedInColorPicker = event.target.closest('input[type="color"]');
            
            if (!clickedInToolbar && !clickedInColorPicker) {
                setExpandedSection(null);
                if (onClose) onClose();
            }
        };
        
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showToolbar, isVisible, onClose]);
    
    // Don't render if not in edit mode or not visible
    if (!isEditMode || !isVisible || !showToolbar) {
        return null;
    }
    
    // Gallery Items
    const galleryItems = element.galleryItems || [];
    
    // Handle Image Upload - FIREBASE STORAGE
    const handleImageUpload = async () => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.multiple = true; // ✅ Multiple images at once
        
        input.onchange = async (e) => {
            const files = Array.from(e.target.files);
            if (files.length === 0 || !onStyleChange) return;
            
            setIsUploadingImage(true);
            
            try {
                const { getStorage, ref: storageRef, uploadBytesResumable, getDownloadURL } = await import('firebase/storage');
                const { getAuth } = await import('firebase/auth');
                
                const storage = getStorage();
                const auth = getAuth();
                
                if (!auth.currentUser) {
                    alert('You must be logged in to upload images.');
                    setIsUploadingImage(false);
                    return;
                }
                
                const userId = auth.currentUser.uid;
                const uploadedItems = [];
                
                // Upload all files
                for (const file of files) {
                    // File Size Check (max 5MB)
                    if (file.size > 5 * 1024 * 1024) {
                        console.warn(`File ${file.name} is too large (max 5MB)`);
                        continue;
                    }
                    
                    const timestamp = Date.now();
                    const randomId = Math.random().toString(36).substr(2, 9);
                    const uniqueFileName = `${timestamp}_${randomId}-${file.name}`;
                    
                    const imageRef = storageRef(storage, `pages/${userId}/gallery/${uniqueFileName}`);
                    const uploadTask = uploadBytesResumable(imageRef, file);
                    
                    // Wait for upload
                    await new Promise((resolve, reject) => {
                        uploadTask.on('state_changed',
                            (snapshot) => {
                                const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
                                console.log(`Upload progress for ${file.name}: ${progress}%`);
                            },
                            (error) => {
                                console.error('Upload error:', error);
                                reject(error);
                            },
                            async () => {
                                try {
                                    const downloadURL = await getDownloadURL(imageRef);
                                    
                                    // Create gallery item (mit allen Text-Feldern)
                                    const newItem = {
                                        id: `gallery-item-${Date.now()}-${randomId}`,
                                        imageUrl: downloadURL,
                                        title: '',
                                        subtitle: '',
                                        description: '',
                                        caption: '',
                                        alt: file.name,
                                        link: '',
                                        order: galleryItems.length + uploadedItems.length,
                                        aspectRatio: 1.5, // Default, can be updated
                                        height: 300 // Default height
                                    };
                                    
                                    uploadedItems.push(newItem);
                                    resolve();
                                } catch (error) {
                                    reject(error);
                                }
                            }
                        );
                    });
                }
                
                // Add all uploaded items to gallery
                if (uploadedItems.length > 0) {
                    onStyleChange({ 
                        galleryItems: [...galleryItems, ...uploadedItems]
                    });
                }
                
                setIsUploadingImage(false);
            } catch (error) {
                console.error('Error uploading images:', error);
                alert('Upload failed. Please try again.');
                setIsUploadingImage(false);
            }
        };
        
        input.click();
    };
    
    // Handle Item Update
    const handleItemUpdate = (itemId, updates) => {
        const updatedItems = galleryItems.map(item => 
            item.id === itemId ? { ...item, ...updates } : item
        );
        onStyleChange({ galleryItems: updatedItems });
    };
    
    // Handle Item Delete
    const handleItemDelete = (itemId) => {
        const updatedItems = galleryItems.filter(item => item.id !== itemId);
        // Reorder
        const reorderedItems = updatedItems.map((item, index) => ({
            ...item,
            order: index
        }));
        onStyleChange({ galleryItems: reorderedItems });
        if (selectedImageId === itemId) {
            setSelectedImageId(null);
        }
    };
    
    // Handle Item Reorder
    const handleItemMove = (itemId, direction) => {
        const currentIndex = galleryItems.findIndex(item => item.id === itemId);
        if (currentIndex === -1) return;
        
        const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
        if (newIndex < 0 || newIndex >= galleryItems.length) return;
        
        const newItems = [...galleryItems];
        [newItems[currentIndex], newItems[newIndex]] = [newItems[newIndex], newItems[currentIndex]];
        
        // Update order
        const reorderedItems = newItems.map((item, index) => ({
            ...item,
            order: index
        }));
        
        onStyleChange({ galleryItems: reorderedItems });
    };
    
    // Selected Image Object
    const selectedImage = selectedImageId 
        ? galleryItems.find(item => item.id === selectedImageId)
        : null;
    
    return ReactDOM.createPortal(
        <div 
            ref={toolbarRef}
            className={`gallery-floating-toolbar-portal ${expandedSection ? 'expanded' : ''} ${isDragging ? 'dragging' : ''}`}
            style={{
                position: 'fixed',
                top: `${toolbarPosition.top}px`,
                left: `${toolbarPosition.left}px`,
                transform: 'translateX(-50%)',
                zIndex: 999999
            }}
        >
            <div 
                className="gallery-toolbar"
                onMouseDown={(e) => {
                    // ✅ Stoppe mousedown Events innerhalb der Toolbar
                    e.stopPropagation();
                }}
            >
                {/* 🎨 MAIN TOOLBAR BUTTONS - Icon Row */}
                <div className="gallery-toolbar-row">
                    {/* 🎯 Drag Handle Icon */}
                    <div 
                        className="gallery-drag-handle"
                        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
                        title="Drag to move toolbar"
                        onMouseDown={handleMouseDown}
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                        }}
                    >
                        <i className="fas fa-grip-vertical"></i>
                    </div>
                    
                    {/* 1. Images */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'images' ? null : 'images');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'images' ? 'is-active' : ''}`}
                        title="Manage Images"
                    >
                        <i className="fas fa-images"></i>
                        {galleryItems.length > 0 && (
                            <span className="gallery-toolbar-badge">{galleryItems.length}</span>
                        )}
                    </button>
                    
                    {/* 2. Layout */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'layout' ? null : 'layout');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'layout' ? 'is-active' : ''}`}
                        title="Layout Settings"
                    >
                        <i className="fas fa-th"></i>
                    </button>
                    
                    {/* 3. Text */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'text' ? null : 'text');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'text' ? 'is-active' : ''} ${(element.galleryShowTitle || element.galleryShowSubtitle || element.galleryShowDescription || element.galleryShowCaption) ? 'has-feature' : ''}`}
                        title="Text Settings"
                    >
                        <i className="fas fa-font"></i>
                    </button>
                    
                    {/* ✨ 3.5 Font Family */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'fontFamily' ? null : 'fontFamily');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'fontFamily' ? 'is-active' : ''}`}
                        title="Font Family"
                    >
                        <i className="fas fa-text-height"></i>
                    </button>
                    
                    {/* 4. Styling */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'styling' ? null : 'styling');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'styling' ? 'is-active' : ''}`}
                        title="Styling Options"
                    >
                        <i className="fas fa-paint-brush"></i>
                    </button>
                    
                    {/* 5. Library */}
                    <button
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setExpandedSection(expandedSection === 'library' ? null : 'library');
                        }}
                        className={`gallery-toolbar-btn ${expandedSection === 'library' ? 'is-active' : ''}`}
                        title="Image Library"
                    >
                        <i className="fas fa-folder-open"></i>
                    </button>
                </div>
                
                {/* EXPANDABLE SECTIONS */}
                
                {/* 1️⃣ IMAGES SECTION */}
                {expandedSection === 'images' && (
                    <div className="gallery-expanded-section">
                        {/* Upload Button */}
                        <button
                            className="gallery-upload-btn-main"
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleImageUpload();
                            }}
                            disabled={isUploadingImage}
                        >
                            {isUploadingImage ? (
                                <>
                                    <div className="gallery-upload-spinner"></div>
                                    <span>Uploading...</span>
                                </>
                            ) : (
                                <>
                                    <i className="fas fa-upload"></i>
                                    <span>Add Images</span>
                                </>
                            )}
                        </button>
                        
                        {/* Images Grid */}
                        {galleryItems.length > 0 ? (
                            <div className="gallery-images-grid">
                                {galleryItems.map((item, index) => (
                                    <div 
                                        key={item.id} 
                                        className={`gallery-image-item ${selectedImageId === item.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedImageId(item.id)}
                                    >
                                        <img src={item.imageUrl} alt={item.alt || 'Gallery'} />
                                        
                                        {/* Item Controls */}
                                        <div className="gallery-image-controls">
                                            <button
                                                className="gallery-image-control-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleItemMove(item.id, 'up');
                                                }}
                                                disabled={index === 0}
                                                title="Move Up"
                                            >
                                                <i className="fas fa-arrow-up"></i>
                                            </button>
                                            <button
                                                className="gallery-image-control-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleItemMove(item.id, 'down');
                                                }}
                                                disabled={index === galleryItems.length - 1}
                                                title="Move Down"
                                            >
                                                <i className="fas fa-arrow-down"></i>
                                            </button>
                                            <button
                                                className="gallery-image-control-btn delete"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (window.confirm('Delete this image from the gallery?')) {
                                                        handleItemDelete(item.id);
                                                    }
                                                }}
                                                title="Delete"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </div>
                                        
                                        {/* Caption Preview */}
                                        {item.caption && (
                                            <div className="gallery-image-caption-preview">
                                                {item.caption}
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="gallery-empty-message">
                                <i className="fas fa-images"></i>
                                <p>No images yet. Click "Add Images" to upload.</p>
                            </div>
                        )}
                        
                        {/* Selected Image Edit */}
                        {selectedImage && (
                            <div className="gallery-image-edit">
                                <div className="gallery-image-edit-header">
                                    <span>Edit Image</span>
                                    <button
                                        className="gallery-image-edit-close"
                                        onClick={() => setSelectedImageId(null)}
                                    >
                                        <i className="fas fa-times"></i>
                                    </button>
                                </div>
                                
                                {/* Caption Input */}
                                <div className="gallery-input-group">
                                    <label>Title</label>
                                    <input
                                        type="text"
                                        value={selectedImage.title || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { title: e.target.value })}
                                        placeholder="Enter image title..."
                                    />
                                </div>
                                
                                {/* Subtitle Input */}
                                <div className="gallery-input-group">
                                    <label>Subtitle</label>
                                    <input
                                        type="text"
                                        value={selectedImage.subtitle || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { subtitle: e.target.value })}
                                        placeholder="Enter subtitle..."
                                    />
                                </div>
                                
                                {/* Description Input */}
                                <div className="gallery-input-group">
                                    <label>Description</label>
                                    <textarea
                                        rows="3"
                                        value={selectedImage.description || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { description: e.target.value })}
                                        placeholder="Enter description..."
                                        style={{
                                            width: '100%',
                                            padding: '8px 12px',
                                            background: 'rgba(255, 255, 255, 0.1)',
                                            border: '1px solid rgba(255, 255, 255, 0.2)',
                                            borderRadius: '6px',
                                            color: 'white',
                                            fontSize: '13px',
                                            resize: 'vertical',
                                            minHeight: '60px'
                                        }}
                                    />
                                </div>
                                
                                {/* Caption Input */}
                                <div className="gallery-input-group">
                                    <label>Caption</label>
                                    <input
                                        type="text"
                                        value={selectedImage.caption || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { caption: e.target.value })}
                                        placeholder="Enter image caption..."
                                    />
                                </div>
                                
                                {/* Alt Text Input */}
                                <div className="gallery-input-group">
                                    <label>Alt Text</label>
                                    <input
                                        type="text"
                                        value={selectedImage.alt || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { alt: e.target.value })}
                                        placeholder="Enter alt text for accessibility..."
                                    />
                                </div>
                                
                                {/* Link Input */}
                                <div className="gallery-input-group">
                                    <label>Link (Optional)</label>
                                    <input
                                        type="url"
                                        value={selectedImage.link || ''}
                                        onChange={(e) => handleItemUpdate(selectedImage.id, { link: e.target.value })}
                                        placeholder="https://example.com"
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}
                
                {/* 2️⃣ LAYOUT SECTION */}
                {expandedSection === 'layout' && (
                    <div className="gallery-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* 🎨 LAYOUT MODE SELECTOR */}
                        <div className="gallery-layout-mode-selector">
                            <div className="gallery-layout-mode-grid">
                                <button
                                    className={`gallery-layout-mode-btn ${
                                        device === 'mobile'
                                            ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel') === 'carousel' ? 'active' : ''
                                            : (element.galleryLayout || 'carousel') === 'carousel' ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        const update = device === 'mobile'
                                            ? { galleryLayoutMobile: 'carousel' }
                                            : { galleryLayout: 'carousel' };
                                        console.log('🔄 Gallery Layout Update:', update, 'Device:', device);
                                        onStyleChange(update);
                                    }}
                                    title="Horizontal scrolling carousel"
                                >
                                    <i className="fas fa-ellipsis-h"></i>
                                    <span>Carousel</span>
                                </button>
                                <button
                                    className={`gallery-layout-mode-btn ${
                                        device === 'mobile'
                                            ? (element.galleryLayoutMobile || element.galleryLayout) === 'masonry' ? 'active' : ''
                                            : element.galleryLayout === 'masonry' ? 'active' : ''
                                    }`}
                                    onClick={() => {
                                        const update = device === 'mobile'
                                            ? { galleryLayoutMobile: 'masonry' }
                                            : { galleryLayout: 'masonry' };
                                        console.log('🔄 Gallery Layout Update:', update, 'Device:', device);
                                        onStyleChange(update);
                                    }}
                                    title="Grid layout with columns"
                                >
                                    <i className="fas fa-th"></i>
                                    <span>Grid Masonry</span>
                                </button>
                            </div>
                        </div>
                        
                        {/* ✅ CAROUSEL SETTINGS (nur wenn Carousel Mode aktiv für aktuelles Device) */}
                        {(() => {
                            const currentLayoutMode = device === 'mobile'
                                ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel')
                                : (element.galleryLayout || 'carousel');
                            return currentLayoutMode === 'carousel';
                        })() && (
                            <>
                                {/* Columns Slider - bestimmt wie viele Items sichtbar sind */}
                                <div className="typo-control">
                                    <label>Visible Items</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="6"
                                        value={device === 'mobile' 
                                            ? (element.galleryColumnsMobile !== undefined ? element.galleryColumnsMobile : 2)
                                            : (element.galleryColumns !== undefined ? element.galleryColumns : 3)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile' 
                                                ? { galleryColumnsMobile: value }
                                                : { galleryColumns: value };
                                            console.log('🔄 Gallery Columns Update:', update, 'Device:', device);
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile' 
                                            ? (element.galleryColumnsMobile !== undefined ? element.galleryColumnsMobile : 2)
                                            : (element.galleryColumns !== undefined ? element.galleryColumns : 3)
                                        }
                                    </span>
                                </div>
                            </>
                        )}
                        
                        {/* ✅ GRID MASONRY SETTINGS (nur wenn Masonry Mode aktiv für aktuelles Device) */}
                        {(() => {
                            const currentLayoutMode = device === 'mobile'
                                ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel')
                                : (element.galleryLayout || 'carousel');
                            return currentLayoutMode === 'masonry';
                        })() && (
                            <>
                                {/* Columns Slider - bestimmt Grid-Spalten */}
                                <div className="typo-control">
                                    <label>Grid Columns</label>
                                    <input
                                        type="range"
                                        min="1"
                                        max="6"
                                        value={device === 'mobile' 
                                            ? (element.galleryColumnsMobile !== undefined ? element.galleryColumnsMobile : 2)
                                            : (element.galleryColumns !== undefined ? element.galleryColumns : 3)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile' 
                                                ? { galleryColumnsMobile: value }
                                                : { galleryColumns: value };
                                            console.log('🔄 Gallery Grid Columns Update:', update, 'Device:', device);
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile' 
                                            ? (element.galleryColumnsMobile !== undefined ? element.galleryColumnsMobile : 2)
                                            : (element.galleryColumns !== undefined ? element.galleryColumns : 3)
                                        }
                                    </span>
                                </div>
                            </>
                        )}
                        
                        {/* Gap Slider */}
                        <div className="typo-control">
                            <label>Gap</label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                step="5"
                                value={device === 'mobile' 
                                    ? (element.galleryGapMobile !== undefined ? element.galleryGapMobile : 15)
                                    : (element.galleryGap !== undefined ? element.galleryGap : 20)
                                }
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    const update = device === 'mobile'
                                        ? { galleryGapMobile: value }
                                        : { galleryGap: value };
                                    console.log('🔄 Gallery Gap Update:', update, 'Device:', device);
                                    onStyleChange(update);
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">
                                {device === 'mobile' 
                                    ? (element.galleryGapMobile !== undefined ? element.galleryGapMobile : 15)
                                    : (element.galleryGap !== undefined ? element.galleryGap : 20)
                                }px
                            </span>
                        </div>
                        
                        {/* Edge Margin Slider - nur für Carousel (device-aware) */}
                        {(() => {
                            const currentLayoutMode = device === 'mobile'
                                ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel')
                                : (element.galleryLayout || 'carousel');
                            return currentLayoutMode === 'carousel';
                        })() && (
                            <div className="typo-control">
                                <label>Edge Margin</label>
                                <input
                                    type="range"
                                    min="0"
                                    max="100"
                                    step="5"
                                    value={device === 'mobile' 
                                        ? (element.galleryEdgeMarginMobile !== undefined ? element.galleryEdgeMarginMobile : 0)
                                        : (element.galleryEdgeMargin !== undefined ? element.galleryEdgeMargin : 0)
                                    }
                                    onChange={(e) => {
                                        const value = parseInt(e.target.value);
                                        const update = device === 'mobile'
                                            ? { galleryEdgeMarginMobile: value }
                                            : { galleryEdgeMargin: value };
                                        console.log('🔄 Gallery Edge Margin Update:', update, 'Device:', device);
                                        onStyleChange(update);
                                    }}
                                    className="typo-slider"
                                />
                                <span className="typo-val">
                                    {device === 'mobile' 
                                        ? (element.galleryEdgeMarginMobile !== undefined ? element.galleryEdgeMarginMobile : 0)
                                        : (element.galleryEdgeMargin !== undefined ? element.galleryEdgeMargin : 0)
                                    }px
                                </span>
                            </div>
                        )}
                        
                        {/* Item Height Slider - nur für Carousel Mode (device-aware) */}
                        {(() => {
                            const currentLayoutMode = device === 'mobile'
                                ? (element.galleryLayoutMobile || element.galleryLayout || 'carousel')
                                : (element.galleryLayout || 'carousel');
                            return currentLayoutMode === 'carousel';
                        })() && (
                            <>
                                <div className="typo-control">
                                    <label>Item Height</label>
                                    <input
                                        type="range"
                                        min="200"
                                        max="600"
                                        step="25"
                                        value={device === 'mobile' 
                                            ? (element.galleryItemHeightMobile !== undefined ? element.galleryItemHeightMobile : 300)
                                            : (element.galleryItemHeight !== undefined ? element.galleryItemHeight : 400)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile'
                                                ? { galleryItemHeightMobile: value }
                                                : { galleryItemHeight: value };
                                            console.log('🔄 Gallery Item Height Update:', update, 'Device:', device);
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile' 
                                            ? (element.galleryItemHeightMobile !== undefined ? element.galleryItemHeightMobile : 300)
                                            : (element.galleryItemHeight !== undefined ? element.galleryItemHeight : 400)
                                        }px
                                    </span>
                                </div>
                                
                                {/* Item Max Width Slider - nur für Carousel */}
                                <div className="typo-control">
                                    <label>Item Max Width</label>
                                    <input
                                        type="range"
                                        min="200"
                                        max="800"
                                        step="25"
                                        value={device === 'mobile' 
                                            ? (element.galleryItemMaxWidthMobile !== undefined ? element.galleryItemMaxWidthMobile : 350)
                                            : (element.galleryItemMaxWidth !== undefined ? element.galleryItemMaxWidth : 500)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile'
                                                ? { galleryItemMaxWidthMobile: value }
                                                : { galleryItemMaxWidth: value };
                                            console.log('🔄 Gallery Item Max Width Update:', update, 'Device:', device);
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile' 
                                            ? (element.galleryItemMaxWidthMobile !== undefined ? element.galleryItemMaxWidthMobile : 350)
                                            : (element.galleryItemMaxWidth !== undefined ? element.galleryItemMaxWidth : 500)
                                        }px
                                    </span>
                                </div>
                            </>
                        )}
                        
                        <span className="gallery-section-label">Layout Settings</span>
                    </div>
                )}
                
                {/* 3️⃣ TEXT SETTINGS SECTION (ERWEITERT) */}
                {expandedSection === 'text' && (
                    <div className="gallery-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Text Layout Modes */}
                        <div className="gallery-text-mode-group">
                            <div className="gallery-text-mode-grid">
                                {[
                                    { value: 'gradient-bottom', label: 'Gradient Bottom', icon: 'chart-area', desc: 'Black gradient overlay at bottom' },
                                    { value: 'below-image', label: 'Below Image', icon: 'arrow-down', desc: 'Clean text below image' },
                                    { value: 'overlay-dark', label: 'Dark Overlay', icon: 'layer-group', desc: 'Semi-transparent dark overlay' },
                                    { value: 'card-below', label: 'Card Below', icon: 'square', desc: 'White card style below' }
                                ].map((mode) => (
                                    <button
                                        key={mode.value}
                                        className={`gallery-text-mode-btn ${(element.galleryTextDisplayMode || 'gradient-bottom') === mode.value ? 'active' : ''}`}
                                        onClick={() => onStyleChange({ galleryTextDisplayMode: mode.value })}
                                        title={mode.desc}
                                    >
                                        <i className={`fas fa-${mode.icon}`}></i>
                                        <span>{mode.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Text Field Toggles - Moderne iOS-Style Switches */}
                        <div className="gallery-text-toggles">
                            
                            {/* Title Toggle */}
                            <div className="gallery-toggle-row">
                                <span className="gallery-toggle-label-text">Title</span>
                                <label className="gallery-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryShowTitle !== false}
                                        onChange={(e) => onStyleChange({ galleryShowTitle: e.target.checked })}
                                    />
                                    <span className="gallery-toggle-slider"></span>
                                </label>
                            </div>
                            
                            {/* Subtitle Toggle */}
                            <div className="gallery-toggle-row">
                                <span className="gallery-toggle-label-text">Subtitle</span>
                                <label className="gallery-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryShowSubtitle !== false}
                                        onChange={(e) => onStyleChange({ galleryShowSubtitle: e.target.checked })}
                                    />
                                    <span className="gallery-toggle-slider"></span>
                                </label>
                            </div>
                            
                            {/* Description Toggle */}
                            <div className="gallery-toggle-row">
                                <span className="gallery-toggle-label-text">Description</span>
                                <label className="gallery-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryShowDescription !== false}
                                        onChange={(e) => onStyleChange({ galleryShowDescription: e.target.checked })}
                                    />
                                    <span className="gallery-toggle-slider"></span>
                                </label>
                            </div>
                            
                            {/* Caption Toggle */}
                            <div className="gallery-toggle-row">
                                <span className="gallery-toggle-label-text">Caption</span>
                                <label className="gallery-toggle-switch">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryShowCaption !== false}
                                        onChange={(e) => onStyleChange({ galleryShowCaption: e.target.checked })}
                                    />
                                    <span className="gallery-toggle-slider"></span>
                                </label>
                            </div>
                        </div>
                        
                        {/* Text Styling */}
                        <div className="gallery-text-styling">
                            {/* Title Size - Device Aware */}
                            {element.galleryShowTitle !== false && (
                                <div className="typo-control">
                                    <label>Title Size</label>
                                    <input
                                        type="range"
                                        min="14"
                                        max="32"
                                        value={device === 'mobile'
                                            ? (typeof element.galleryTitleSizeMobile === 'number' ? element.galleryTitleSizeMobile : 16)
                                            : (typeof element.galleryTitleSize === 'number' ? element.galleryTitleSize : 18)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile'
                                                ? { galleryTitleSizeMobile: value }
                                                : { galleryTitleSize: value };
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile'
                                            ? (typeof element.galleryTitleSizeMobile === 'number' ? element.galleryTitleSizeMobile : 16)
                                            : (typeof element.galleryTitleSize === 'number' ? element.galleryTitleSize : 18)
                                        }px
                                    </span>
                                </div>
                            )}
                            
                            {/* Subtitle Size - Device Aware */}
                            {element.galleryShowSubtitle !== false && (
                                <div className="typo-control">
                                    <label>Subtitle Size</label>
                                    <input
                                        type="range"
                                        min="10"
                                        max="24"
                                        value={device === 'mobile'
                                            ? (typeof element.gallerySubtitleSizeMobile === 'number' ? element.gallerySubtitleSizeMobile : 12)
                                            : (typeof element.gallerySubtitleSize === 'number' ? element.gallerySubtitleSize : 14)
                                        }
                                        onChange={(e) => {
                                            const value = parseInt(e.target.value);
                                            const update = device === 'mobile'
                                                ? { gallerySubtitleSizeMobile: value }
                                                : { gallerySubtitleSize: value };
                                            onStyleChange(update);
                                        }}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile'
                                            ? (typeof element.gallerySubtitleSizeMobile === 'number' ? element.gallerySubtitleSizeMobile : 12)
                                            : (typeof element.gallerySubtitleSize === 'number' ? element.gallerySubtitleSize : 14)
                                        }px
                                    </span>
                                </div>
                            )}
                            
                            {/* Description/Caption Font Size */}
                            {(element.galleryShowDescription !== false || element.galleryShowCaption !== false) && (
                                <div className="typo-control">
                                    <label>Text Size</label>
                                    <input
                                        type="range"
                                        min="10"
                                        max="20"
                                        value={device === 'mobile' 
                                            ? (typeof element.galleryCaptionFontSizeMobile === 'number' ? element.galleryCaptionFontSizeMobile : 12)
                                            : (typeof element.galleryCaptionFontSize === 'number' ? element.galleryCaptionFontSize : 14)
                                        }
                                        onChange={(e) => onStyleChange({
                                            [device === 'mobile' ? 'galleryCaptionFontSizeMobile' : 'galleryCaptionFontSize']: parseInt(e.target.value)
                                        })}
                                        className="typo-slider"
                                    />
                                    <span className="typo-val">
                                        {device === 'mobile' 
                                            ? (typeof element.galleryCaptionFontSizeMobile === 'number' ? element.galleryCaptionFontSizeMobile : 12)
                                            : (typeof element.galleryCaptionFontSize === 'number' ? element.galleryCaptionFontSize : 14)
                                        }px
                                    </span>
                                </div>
                            )}
                        </div>
                        
                        {/* Text Color */}
                        <div className="gallery-text-colors">
                            <div className="gallery-color-group">
                                <label className="gallery-color-label">Text Color</label>
                                <div className="button-brand-colors" style={{ marginTop: '8px' }}>
                                    {Object.values(colors).map((brandColor, index) => (
                                        <button
                                            key={index}
                                            type="button"
                                            className={`button-brand-color ${element.galleryTextColor === brandColor ? 'active' : ''}`}
                                            style={{ backgroundColor: brandColor }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ galleryTextColor: brandColor });
                                            }}
                                            title={`Text Color ${index + 1}`}
                                        />
                                    ))}
                                </div>
                            </div>
                            
                            {/* ✨ Text Background Color - für Overlay/Gradient Modes */}
                            {(element.galleryTextDisplayMode === 'gradient-bottom' || 
                              element.galleryTextDisplayMode === 'overlay-dark' ||
                              element.galleryTextDisplayMode === 'card-below') && (
                                <div className="gallery-color-group" style={{ marginTop: '16px' }}>
                                    <label className="gallery-color-label">
                                        <i className="fas fa-fill-drip" style={{ marginRight: '6px' }}></i>
                                        {element.galleryTextDisplayMode === 'card-below' ? 'Card Background' : 'Overlay Background'}
                                    </label>
                                    <div className="button-brand-colors" style={{ marginTop: '8px' }}>
                                        {/* Transparent */}
                                        <button
                                            type="button"
                                            className={`button-brand-color ${element.galleryTextBackgroundColor === 'transparent' ? 'active' : ''}`}
                                            style={{ 
                                                background: 'transparent',
                                                border: '2px dashed rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                onStyleChange({ galleryTextBackgroundColor: 'transparent' });
                                            }}
                                            title="Transparent"
                                        >
                                            <i className="fas fa-slash" style={{ fontSize: '14px', color: 'rgba(255, 255, 255, 0.5)' }}></i>
                                        </button>
                                        
                                        {/* Brand Colors */}
                                        {Object.values(colors).map((brandColor, index) => (
                                            <button
                                                key={index}
                                                type="button"
                                                className={`button-brand-color ${element.galleryTextBackgroundColor === brandColor ? 'active' : ''}`}
                                                style={{ backgroundColor: brandColor }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ galleryTextBackgroundColor: brandColor });
                                                }}
                                                title={`Background Color ${index + 1}`}
                                            />
                                        ))}
                                        
                                        {/* Custom Color Picker */}
                                        <button
                                            type="button"
                                            className="button-brand-color"
                                            style={{ 
                                                background: 'rgba(255, 255, 255, 0.1)',
                                                border: '2px solid rgba(255, 255, 255, 0.3)'
                                            }}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                document.getElementById('gallery-bg-color-picker').click();
                                            }}
                                            title="Custom Color"
                                        >
                                            <i className="fas fa-palette" style={{ fontSize: '14px', color: 'white' }}></i>
                                            <input
                                                id="gallery-bg-color-picker"
                                                type="color"
                                                value={element.galleryTextBackgroundColor || '#000000'}
                                                style={{ display: 'none' }}
                                                onChange={(e) => {
                                                    onStyleChange({ galleryTextBackgroundColor: e.target.value });
                                                }}
                                            />
                                        </button>
                                    </div>
                                    
                                    {/* Background Opacity Slider */}
                                    <div className="typo-control" style={{ marginTop: '12px' }}>
                                        <label>Background Opacity</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="100"
                                            step="5"
                                            value={element.galleryTextBackgroundOpacity ?? 85}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                onStyleChange({ galleryTextBackgroundOpacity: value });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {element.galleryTextBackgroundOpacity ?? 85}%
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        <span className="gallery-section-label">Text Settings</span>
                    </div>
                )}
                
                {/* 3.5️⃣ FONT FAMILY SECTION */}
                {expandedSection === 'fontFamily' && (
                    <div className="gallery-expanded-section">
                        {/* Title Font Family */}
                        <div className="gallery-font-section">
                            <label className="gallery-color-label" style={{ marginBottom: '12px' }}>
                                <i className="fas fa-heading" style={{ marginRight: '6px' }}></i>
                                Title Font
                            </label>
                            <div className="tiptap-font-family-list">
                                {/* ⭐ BRAND FONTS ZUERST */}
                                {brandFonts?.globalBigFont && (
                                    <button
                                        type="button"
                                        className={`tiptap-font-family-option brand-font ${element.galleryTitleFontFamily === brandFonts.globalBigFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalBigFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTitleFontFamily: brandFonts.globalBigFont });
                                        }}
                                        title={`${brandFonts.globalBigFont} (Brand Headline Font)`}
                                    >
                                        ⭐ {brandFonts.globalBigFont}
                                    </button>
                                )}
                                
                                {brandFonts?.globalSmallFont && brandFonts.globalSmallFont !== brandFonts.globalBigFont && (
                                    <button
                                        type="button"
                                        className={`tiptap-font-family-option brand-font ${element.galleryTitleFontFamily === brandFonts.globalSmallFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalSmallFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTitleFontFamily: brandFonts.globalSmallFont });
                                        }}
                                        title={`${brandFonts.globalSmallFont} (Brand Body Font)`}
                                    >
                                        ⭐ {brandFonts.globalSmallFont}
                                    </button>
                                )}
                                
                                {/* Divider */}
                                {(brandFonts?.globalBigFont || brandFonts?.globalSmallFont) && (
                                    <div style={{
                                        height: '1px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        margin: '8px 0'
                                    }}></div>
                                )}
                                
                                {/* Custom Fonts */}
                                {fonts.customFonts && fonts.customFonts.length > 0 && (
                                    <>
                                        {fonts.customFonts
                                            .filter(customFont => 
                                                customFont.name !== brandFonts?.globalBigFont && 
                                                customFont.name !== brandFonts?.globalSmallFont
                                            )
                                            .map((customFont, index) => (
                                            <button
                                                key={`custom-${index}`}
                                                type="button"
                                                className={`tiptap-font-family-option ${element.galleryTitleFontFamily === customFont.name ? 'is-active' : ''}`}
                                                style={{ fontFamily: customFont.name }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ galleryTitleFontFamily: customFont.name });
                                                }}
                                                title={customFont.name}
                                            >
                                                {customFont.name}
                                            </button>
                                        ))}
                                    </>
                                )}

                                {/* Title Fonts */}
                                {titleFonts
                                    .filter(font => 
                                        font !== brandFonts?.globalBigFont && 
                                        font !== brandFonts?.globalSmallFont
                                    )
                                    .map((font, index) => (
                                    <button
                                        key={`title-${index}`}
                                        type="button"
                                        className={`tiptap-font-family-option ${element.galleryTitleFontFamily === font ? 'is-active' : ''}`}
                                        style={{ fontFamily: font }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTitleFontFamily: font });
                                        }}
                                        title={font}
                                    >
                                        {font}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Divider zwischen Title und Text Fonts */}
                        <div style={{
                            height: '1px',
                            background: 'rgba(255, 255, 255, 0.2)',
                            margin: '20px 0'
                        }}></div>
                        
                        {/* Text Font Family (für Subtitle, Description, Caption) */}
                        <div className="gallery-font-section">
                            <label className="gallery-color-label" style={{ marginBottom: '12px' }}>
                                <i className="fas fa-paragraph" style={{ marginRight: '6px' }}></i>
                                Text Font (Subtitle, Description, Caption)
                            </label>
                            <div className="tiptap-font-family-list">
                                {/* ⭐ BRAND FONTS ZUERST */}
                                {brandFonts?.globalSmallFont && (
                                    <button
                                        type="button"
                                        className={`tiptap-font-family-option brand-font ${element.galleryTextFontFamily === brandFonts.globalSmallFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalSmallFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTextFontFamily: brandFonts.globalSmallFont });
                                        }}
                                        title={`${brandFonts.globalSmallFont} (Brand Body Font)`}
                                    >
                                        ⭐ {brandFonts.globalSmallFont}
                                    </button>
                                )}
                                
                                {brandFonts?.globalBigFont && brandFonts.globalBigFont !== brandFonts.globalSmallFont && (
                                    <button
                                        type="button"
                                        className={`tiptap-font-family-option brand-font ${element.galleryTextFontFamily === brandFonts.globalBigFont ? 'is-active' : ''}`}
                                        style={{ fontFamily: brandFonts.globalBigFont }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTextFontFamily: brandFonts.globalBigFont });
                                        }}
                                        title={`${brandFonts.globalBigFont} (Brand Headline Font)`}
                                    >
                                        ⭐ {brandFonts.globalBigFont}
                                    </button>
                                )}
                                
                                {/* Divider */}
                                {(brandFonts?.globalBigFont || brandFonts?.globalSmallFont) && (
                                    <div style={{
                                        height: '1px',
                                        background: 'rgba(255, 255, 255, 0.2)',
                                        margin: '8px 0'
                                    }}></div>
                                )}
                                
                                {/* Custom Fonts */}
                                {fonts.customFonts && fonts.customFonts.length > 0 && (
                                    <>
                                        {fonts.customFonts
                                            .filter(customFont => 
                                                customFont.name !== brandFonts?.globalBigFont && 
                                                customFont.name !== brandFonts?.globalSmallFont
                                            )
                                            .map((customFont, index) => (
                                            <button
                                                key={`custom-${index}`}
                                                type="button"
                                                className={`tiptap-font-family-option ${element.galleryTextFontFamily === customFont.name ? 'is-active' : ''}`}
                                                style={{ fontFamily: customFont.name }}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    onStyleChange({ galleryTextFontFamily: customFont.name });
                                                }}
                                                title={customFont.name}
                                            >
                                                {customFont.name}
                                            </button>
                                        ))}
                                    </>
                                )}

                                {/* Text Fonts */}
                                {textFonts
                                    .filter(font => 
                                        font !== brandFonts?.globalBigFont && 
                                        font !== brandFonts?.globalSmallFont
                                    )
                                    .map((font, index) => (
                                    <button
                                        key={`text-${index}`}
                                        type="button"
                                        className={`tiptap-font-family-option ${element.galleryTextFontFamily === font ? 'is-active' : ''}`}
                                        style={{ fontFamily: font }}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            onStyleChange({ galleryTextFontFamily: font });
                                        }}
                                        title={font}
                                    >
                                        {font}
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        <span className="gallery-section-label">Font Family</span>
                    </div>
                )}
                
                {/* 4️⃣ STYLING SECTION */}
                {expandedSection === 'styling' && (
                    <div className="gallery-expanded-section">
                        {/* ✅ Desktop/Mobile Toggle */}
                        <div className="floating-typo-device-row">
                            <span className="floating-typo-device-label">Desktop</span>
                            <div 
                                className={`floating-typo-toggle ${device === 'mobile' ? 'mobile' : ''}`}
                                onClick={() => setDevice(device === 'desktop' ? 'mobile' : 'desktop')}
                            >
                                <div className="floating-typo-toggle-slider"></div>
                            </div>
                            <span className="floating-typo-device-label">Mobile</span>
                        </div>
                        
                        {/* Border Radius */}
                        <div className="typo-control">
                            <label>Border Radius</label>
                            <input
                                type="range"
                                min="0"
                                max="50"
                                value={device === 'mobile' 
                                    ? (typeof element.galleryItemBorderRadiusMobile === 'number' ? element.galleryItemBorderRadiusMobile : 12)
                                    : (typeof element.galleryItemBorderRadius === 'number' ? element.galleryItemBorderRadius : 12)
                                }
                                onChange={(e) => {
                                    const value = parseInt(e.target.value);
                                    const update = device === 'mobile'
                                        ? { galleryItemBorderRadiusMobile: value }
                                        : { galleryItemBorderRadius: value };
                                    console.log('🔄 Gallery Border Radius Update:', update, 'Device:', device);
                                    onStyleChange(update);
                                }}
                                className="typo-slider"
                            />
                            <span className="typo-val">
                                {device === 'mobile' 
                                    ? (typeof element.galleryItemBorderRadiusMobile === 'number' ? element.galleryItemBorderRadiusMobile : 12)
                                    : (typeof element.galleryItemBorderRadius === 'number' ? element.galleryItemBorderRadius : 12)
                                }px
                            </span>
                        </div>
                        
                        {/* Image Fit */}
                        <div className="gallery-fit-group">
                            <div className="gallery-fit-buttons">
                                <button
                                    className={`gallery-fit-btn ${(element.galleryImageFit || 'cover') === 'cover' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ galleryImageFit: 'cover' })}
                                >
                                    Cover
                                </button>
                                <button
                                    className={`gallery-fit-btn ${element.galleryImageFit === 'contain' ? 'active' : ''}`}
                                    onClick={() => onStyleChange({ galleryImageFit: 'contain' })}
                                >
                                    Contain
                                </button>
                            </div>
                        </div>
                        
                        {/* Hover Effect */}
                        <div className="gallery-hover-group">
                            <div className="gallery-hover-buttons">
                                {[
                                    { value: 'zoom', label: 'Zoom', icon: 'search-plus' },
                                    { value: 'lift', label: 'Lift', icon: 'arrow-up' },
                                    { value: 'fade', label: 'Fade', icon: 'adjust' },
                                    { value: 'none', label: 'None', icon: 'ban' }
                                ].map((effect) => (
                                    <button
                                        key={effect.value}
                                        className={`gallery-hover-btn ${(element.galleryHoverEffect || 'zoom') === effect.value ? 'active' : ''}`}
                                        onClick={() => onStyleChange({ galleryHoverEffect: effect.value })}
                                    >
                                        <i className={`fas fa-${effect.icon}`}></i>
                                        <span>{effect.label}</span>
                                    </button>
                                ))}
                            </div>
                        </div>
                        
                        {/* Lightbox Toggle */}
                        <div className="gallery-effect-group">
                            <div className="gallery-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryLightboxEnabled !== false}
                                        onChange={(e) => onStyleChange({ galleryLightboxEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Lightbox</span>
                                </label>
                            </div>
                        </div>
                        
                        <div className="gallery-effect-divider" />
                        
                        {/* Animations Toggle */}
                        <div className="gallery-effect-group">
                            <div className="gallery-effect-header">
                                <label className="btn-custom-toggle-wrapper">
                                    <input
                                        type="checkbox"
                                        checked={element.galleryAnimationsEnabled !== false}
                                        onChange={(e) => onStyleChange({ galleryAnimationsEnabled: e.target.checked })}
                                        className="btn-custom-toggle-input"
                                    />
                                    <span className="btn-custom-toggle-slider"></span>
                                    <span className="btn-custom-toggle-label">Animations</span>
                                </label>
                            </div>
                            
                            {/* Animation Delay */}
                            {element.galleryAnimationsEnabled !== false && (
                                <div className="gallery-effect-controls">
                                    <div className="typo-control">
                                        <label>Delay</label>
                                        <input
                                            type="range"
                                            min="0"
                                            max="300"
                                            step="50"
                                            value={typeof element.galleryAnimationDelay === 'number' ? element.galleryAnimationDelay : 100}
                                            onChange={(e) => {
                                                const value = parseInt(e.target.value);
                                                console.log('🔄 Gallery Animation Delay Update:', value);
                                                onStyleChange({ galleryAnimationDelay: value });
                                            }}
                                            className="typo-slider"
                                        />
                                        <span className="typo-val">
                                            {typeof element.galleryAnimationDelay === 'number' ? element.galleryAnimationDelay : 100}ms
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {/* Carousel Navigation Settings - nur im Carousel Mode */}
                        {(element.galleryLayout || 'carousel') === 'carousel' && (
                            <>
                                <div className="gallery-effect-divider" />
                                
                                {/* Show Navigation Toggle */}
                                <div className="gallery-effect-group">
                                    <div className="gallery-effect-header">
                                        <label className="btn-custom-toggle-wrapper">
                                            <input
                                                type="checkbox"
                                                checked={element.galleryShowCarouselNav !== false}
                                                onChange={(e) => onStyleChange({ galleryShowCarouselNav: e.target.checked })}
                                                className="btn-custom-toggle-input"
                                            />
                                            <span className="btn-custom-toggle-slider"></span>
                                            <span className="btn-custom-toggle-label">Show Nav</span>
                                        </label>
                                    </div>
                                    
                                    {/* Navigation Settings (wenn sichtbar) */}
                                    {element.galleryShowCarouselNav !== false && (
                                        <div className="gallery-effect-controls">
                                            {/* Navigation Style */}
                                            <div className="gallery-nav-style-buttons">
                                                <button
                                                    className={`gallery-nav-style-btn ${(element.galleryNavStyle || 'light') === 'light' ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ galleryNavStyle: 'light' });
                                                    }}
                                                >
                                                    <i className="fas fa-circle" style={{ color: 'white' }}></i>
                                                    <span>Light</span>
                                                </button>
                                                <button
                                                    className={`gallery-nav-style-btn ${element.galleryNavStyle === 'dark' ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        onStyleChange({ galleryNavStyle: 'dark' });
                                                    }}
                                                >
                                                    <i className="fas fa-circle" style={{ color: '#333' }}></i>
                                                    <span>Dark</span>
                                                </button>
                                            </div>
                                            
                                            {/* Arrow Roundness */}
                                            <div className="typo-control">
                                                <label>Arrow Roundness</label>
                                                <input
                                                    type="range"
                                                    min="0"
                                                    max="50"
                                                    step="5"
                                                    value={typeof element.galleryNavBorderRadius === 'number' ? element.galleryNavBorderRadius : 0}
                                                    onChange={(e) => onStyleChange({ galleryNavBorderRadius: parseInt(e.target.value) })}
                                                    className="typo-slider"
                                                />
                                                <span className="typo-val">
                                                    {typeof element.galleryNavBorderRadius === 'number' ? element.galleryNavBorderRadius : 0}px
                                                </span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                        
                        <span className="gallery-section-label">Styling</span>
                    </div>
                )}
                
                {/* 5️⃣ LIBRARY SECTION */}
                {expandedSection === 'library' && (
                    <div className="gallery-expanded-section">
                        <div className="gallery-library-placeholder">
                            <i className="fas fa-folder-open"></i>
                            <h4>Image Library</h4>
                            <p>Browse and select images from your library</p>
                            <span className="gallery-coming-soon">Coming Soon</span>
                        </div>
                        
                        <span className="gallery-section-label">Library</span>
                    </div>
                )}
            </div>
        </div>,
        document.body
    );
}

export default GalleryFloatingToolbar;

